import React from 'react'

import { Tooltip } from '@mantine/core'

import { getExpirationLabel } from 'src/lib/expirationTracking'
import { formatCalendarDate } from 'src/utils/date/toMonthYearKey'

interface ExpirationIndicatorProps {
  expirationDate: string | Date | null
  isRenewed?: boolean
  isTrackedCategory?: boolean
  variant?: 'full' | 'dot'
  textClassName?: string
  missingLabel?: string
}

const StatusDot = React.forwardRef<
  HTMLDivElement,
  { color: string; standalone?: boolean } & React.HTMLAttributes<HTMLDivElement>
>(({ color, standalone = false, ...props }, ref) => (
  <div
    ref={ref}
    className={`flex shrink-0 items-center justify-center ${
      standalone ? 'p-[3px]' : ''
    }`}
    {...props}
  >
    <div
      className="h-[7px] w-[7px] rounded-full"
      style={{ backgroundColor: `var(--mantine-color-${color}-filled)` }}
    />
  </div>
))

const ExpirationIndicator = ({
  expirationDate,
  isRenewed = false,
  isTrackedCategory = false,
  variant = 'full',
  textClassName,
  missingLabel,
}: ExpirationIndicatorProps) => {
  const emptyLabel = missingLabel ?? '—'
  const dateTextClass = textClassName ?? 'text-doubleNickel-gray-900'
  const emptyTextClass = textClassName ?? 'text-doubleNickel-gray-400'
  const expInfo = getExpirationLabel(expirationDate, isTrackedCategory)
  const showIndicator =
    !isRenewed &&
    (expInfo.status === 'expired' ||
      expInfo.status === 'expiring' ||
      expInfo.status === 'missing')

  if (!showIndicator) {
    if (variant === 'dot') {
      if (!expirationDate || isRenewed) return null
      return (
        <Tooltip
          label={
            <div className="flex flex-col gap-1">
              <div className="text-xs">{expInfo.label}</div>
              <div className="text-xs text-doubleNickel-gray-400">
                {formatCalendarDate(expirationDate, 'MMM D, YYYY')}
              </div>
            </div>
          }
          withArrow
          position="top"
        >
          <StatusDot color={expInfo.color} standalone />
        </Tooltip>
      )
    }

    const formattedDate = formatCalendarDate(expirationDate)

    if (!formattedDate) {
      return (
        <div className="flex flex-row items-center gap-1.5">
          <span
            className={`inline-block min-w-[4.5rem] cursor-default text-center text-xs ${emptyTextClass}`}
          >
            {emptyLabel}
          </span>
          <div className="h-[7px] w-[7px] shrink-0" />
        </div>
      )
    }

    if (isRenewed) {
      return (
        <div className="flex flex-row items-center gap-1.5">
          <span
            className={`inline-block min-w-[4.5rem] cursor-default text-center text-xs tabular-nums ${dateTextClass}`}
          >
            {formattedDate}
          </span>
          <div className="h-[7px] w-[7px] shrink-0" />
        </div>
      )
    }

    return (
      <Tooltip label={expInfo.label} withArrow position="top">
        <div className="flex flex-row items-center gap-1.5">
          <span
            className={`inline-block min-w-[4.5rem] cursor-default text-center text-xs tabular-nums ${dateTextClass}`}
          >
            {formattedDate}
          </span>
          <StatusDot color={expInfo.color} />
        </div>
      </Tooltip>
    )
  }

  if (variant === 'dot') {
    return (
      <Tooltip
        label={
          <div className="flex flex-col gap-1">
            <div className="text-xs">{expInfo.label}</div>
            {expirationDate && (
              <div className="text-xs text-doubleNickel-gray-400">
                {formatCalendarDate(expirationDate, 'MMM D, YYYY')}
              </div>
            )}
          </div>
        }
        withArrow
        position="top"
      >
        <StatusDot color={expInfo.color} standalone />
      </Tooltip>
    )
  }

  return (
    <Tooltip label={expInfo.label} withArrow position="top">
      <div className="flex flex-row items-center gap-1.5">
        <span
          className={`inline-block min-w-[4.5rem] cursor-default text-center text-xs tabular-nums ${
            expirationDate ? dateTextClass : emptyTextClass
          }`}
        >
          {formatCalendarDate(expirationDate) ?? emptyLabel}
        </span>
        <StatusDot color={expInfo.color} />
      </div>
    </Tooltip>
  )
}

export default ExpirationIndicator
